// Hedef takibi + disclosure hazırlık UI saf mantığı ve TEK kopya kaynağı (TODO-KPI-01/08;
// kpi-ui.ts kalıbı). Bileşenler ince kalır: biçimleme, ton, ilerleme etiketi, hedef düzenleyici
// taslağı ↔ DTO dönüşümü burada. Hesap sunucuda (`services/kpi/targets.ts`) — FE yalnız biçimler.

import {
  formatKpiNumber,
  KPI_DISCLOSURE_ROW_STATUS_LABELS,
  KPI_TARGET_STATUS_LABELS,
  KPI_TARGET_TYPE_LABELS,
  type KpiDisclosureRow,
  type KpiDisclosureRowStatus,
  type KpiDisclosureSummary,
  type KpiTargetProgress,
  type KpiTargetStatus,
  type KpiTargetType,
  type KpiTargetUpsert,
  type KpiTargetWithProgress,
} from "@mahir/shared";

export const KPI_TARGETS_COPY = {
  titles: { hedefler: "Hedefler", disclosure: "Disclosure Hazırlık" },
  menu: { hedefler: "Hedefler", disclosure: "Disclosure Hazırlık" },
  breadcrumb: {
    hedefler: "Müşteriler · Performans Göstergeleri · Hedefler",
    disclosure: "Müşteriler · Performans Göstergeleri · Disclosure Hazırlık",
  },
  targets: {
    /** TODO-KPI-96: birim engelinden gelen `?metricId=` süzgeci */
    filteredBy: (label: string) => `Süzgeç: ${label}`,
    clearFilter: "Tümünü göster",
    subtitle:
      "Gösterge başına hedef yılı ve hedef değer; gerçekleşen değer Veri Girişi'ndeki en son dolu yıldan okunur.",
    strip: (s: { total: number; withTarget: number; achieved: number; onTrack: number }) =>
      `${s.total} gösterge · ${s.withTarget} hedefli · ${s.achieved} ulaşıldı · ${s.onTrack} yolda`,
    columns: {
      indicator: "Gösterge",
      target: "Hedef",
      current: "Gerçekleşen",
      distance: "Hedefe uzaklık",
      progress: "İlerleme",
      owner: "Sahip",
      status: "Durum",
      actions: "",
    },
    noTarget: "Hedef yok",
    addTarget: "+ Hedef ekle",
    edit: "Düzenle",
    remove: "Sil",
    save: "Kaydet",
    cancel: "Vazgeç",
    saving: "Kaydediliyor…",
    deleteTitle: "Hedef silinsin mi?",
    deleteBody: "Hedef satırı kaldırılır; Veri Girişi'ndeki değerler etkilenmez.",
    deleteConfirm: "Sil",
    empty: "Bu veri setinde gösterge yok.",
    noCurrent: "veri yok",
    currentFrom: (year: number) => `${year} değeri`,
    baselineFrom: (year: number) => `baz ${year}`,
    perEntity: "İşletme",
    consolidated: "Konsolide",
    fields: {
      type: "Hedef türü",
      entity: "Kapsam (işletme)",
      baselineYear: "Baz yıl",
      baselineValue: "Baz değer (boşsa Veri Girişi'nden okunur)",
      targetYear: "Hedef yılı",
      targetValue: "Hedef değer",
      targetChangePct: "% değişim (baz yıla göre, azalış için eksi)",
      scope: "Kapsam notu",
      owner: "Sahip",
      status: "Durum",
      note: "Not",
    },
    validation: {
      targetValue: "Hedef değer zorunlu",
      targetChangePct: "% değişim zorunlu",
      baselineYear: "Baz yıl zorunlu ve hedef yılından önce olmalı",
    },
    achieved: "Ulaşıldı",
    onTrack: "Yolda",
    behind: "Geride",
    unknown: "—",
    loadFailed: "Hedefler yüklenemedi",
    ozetHeaders: { target: "Hedef", distance: "Uzaklık" },
  },
  disclosure: {
    subtitle:
      "Seçili standardın katalogdaki gösterge eşlemesi (GRI/TSRS/SPK kodları) × bu veri setinin cari yıl doluluğu.",
    standardLabel: "Standart",
    yearLabel: "Yıl",
    cards: {
      ready: "Hazır",
      required: "Gereken gösterge",
      missing: "Eksik (veri setinde, dolu değil)",
      missingFromSet: "Veri setinde olmayan",
    },
    readyHint: (filled: number, notMeasured: number, required: number) =>
      notMeasured > 0
        ? `${filled} dolu + ${notMeasured} ölçülmüyor / ${required}`
        : `${filled} dolu / ${required}`,
    tabs: {
      missing: "Eksikler",
      all: "Tüm eşlemeler",
      notInSet: "Veri setinde olmayan gerekli göstergeler",
    },
    columns: {
      code: "Standart kodu",
      indicator: "Gösterge",
      category: "Kategori",
      mapping: "Eşleme",
      status: "Durum",
      value: "Değer",
    },
    mapping: { direct: "Doğrudan", partial: "Kısmi" } as Record<string, string>,
    addToSet: "Veri setine ekle",
    adding: "Ekleniyor…",
    added: "Eklendi",
    noneMissing: "Bu standart için eksik gösterge yok — seçili yıl için tüm eşlemeler dolu.",
    noneNotInSet: "Katalogdaki tüm gerekli göstergeler veri setinde.",
    noStandards: "Katalogda standart eşlemesi yok.",
    rowHint: "Satıra tıkla → Veri Girişi'nde o göstergeye git",
    loadFailed: "Disclosure özeti yüklenemedi",
  },
} as const;

export type TargetTone = "achieved" | "onTrack" | "behind" | "unknown";

/** Ulaşıldı → yeşil; ilerleme ≥ 0,5 → yolda; aksi geride; hesaplanamıyorsa nötr. */
export function targetTone(p: Pick<KpiTargetProgress, "achieved" | "progress">): TargetTone {
  if (p.achieved === true) return "achieved";
  if (p.progress === null) return "unknown";
  return p.progress >= 0.5 ? "onTrack" : "behind";
}

export function targetToneLabel(tone: TargetTone): string {
  const t = KPI_TARGETS_COPY.targets;
  return tone === "achieved"
    ? t.achieved
    : tone === "onTrack"
      ? t.onTrack
      : tone === "behind"
        ? t.behind
        : t.unknown;
}

export function targetToneColor(tone: TargetTone): string {
  return tone === "achieved"
    ? "var(--color-success)"
    : tone === "onTrack"
      ? "var(--color-primary)"
      : tone === "behind"
        ? "var(--color-warning-text)"
        : "var(--color-text-3)";
}

export function formatTargetNumber(
  v: number | null | undefined,
  opts: { unit?: string | null; percent?: boolean },
): string {
  if (v === null || v === undefined) return "—";
  return formatKpiNumber(v, {
    percent: opts.percent ?? false,
    unit: opts.percent ? undefined : (opts.unit ?? undefined),
  });
}

/** Hedef hücresi: "700 tCO2e (2030)" / relative'de "−%30 → 700 tCO2e (2030)". */
export function formatTargetCell(t: KpiTargetWithProgress): string {
  const isPct = t.valueType === "percent";
  const eff = formatTargetNumber(t.progress.effectiveTargetValue, { unit: t.unit, percent: isPct });
  const year = t.targetYear ? ` (${t.targetYear})` : "";
  if (t.targetType === "relative") {
    const pct =
      t.targetChangePct === null
        ? "—"
        : `${t.targetChangePct > 0 ? "+" : t.targetChangePct < 0 ? "−" : ""}${formatKpiNumber(Math.abs(t.targetChangePct), { percent: true })}`;
    return `${pct} → ${eff}${year}`;
  }
  return `${eff}${year}`;
}

/** "Hedefe uzaklık": gerçekleşen/hedef % + işaretli fark; veri yoksa "—". */
export function formatDistance(t: KpiTargetWithProgress): string {
  const p = t.progress;
  if (p.currentValue === null || p.effectiveTargetValue === null) return "—";
  const isPct = t.valueType === "percent";
  const gap =
    p.gap === null ? "" : formatTargetNumber(Math.abs(p.gap), { unit: t.unit, percent: isPct });
  const sign = p.gap === null ? "" : p.gap > 0 ? "+" : p.gap < 0 ? "−" : "";
  const ratio =
    p.attainmentPct === null
      ? ""
      : `${formatKpiNumber(p.attainmentPct, { percent: true, decimals: 0 })} · `;
  if (p.gap === 0) return `${ratio}hedefte`;
  return `${ratio}${sign}${gap}`;
}

export function progressPct(p: Pick<KpiTargetProgress, "progress">): number {
  return p.progress === null ? 0 : Math.round(p.progress * 100);
}

export const TARGET_TYPE_OPTIONS: { value: KpiTargetType; label: string }[] = (
  Object.keys(KPI_TARGET_TYPE_LABELS) as KpiTargetType[]
).map((value) => ({ value, label: KPI_TARGET_TYPE_LABELS[value] }));

export const TARGET_STATUS_OPTIONS: { value: KpiTargetStatus; label: string }[] = (
  Object.keys(KPI_TARGET_STATUS_LABELS) as KpiTargetStatus[]
).map((value) => ({ value, label: KPI_TARGET_STATUS_LABELS[value] }));

/** Satır içi düzenleyici taslağı — tüm sayısal alanlar string (boş = null). */
export interface TargetDraft {
  id?: string;
  metricId: string;
  entityId: string;
  targetType: KpiTargetType;
  baselineYear: string;
  baselineValue: string;
  targetYear: string;
  targetValue: string;
  targetChangePct: string;
  scope: string;
  owner: string;
  status: KpiTargetStatus;
  note: string;
}

export function emptyDraft(metricId: string, now = new Date().getFullYear()): TargetDraft {
  return {
    metricId,
    entityId: "",
    targetType: "absolute",
    baselineYear: "",
    baselineValue: "",
    targetYear: String(now + 1),
    targetValue: "",
    targetChangePct: "",
    scope: "",
    owner: "",
    status: "active",
    note: "",
  };
}

export function draftFromTarget(t: KpiTargetWithProgress): TargetDraft {
  const s = (v: number | null): string => (v === null ? "" : String(v));
  return {
    id: t.id,
    metricId: t.metricId,
    entityId: t.entityId ?? "",
    targetType: t.targetType,
    baselineYear: s(t.baselineYear),
    baselineValue: s(t.baselineValue),
    targetYear: s(t.targetYear),
    targetValue: s(t.targetValue),
    targetChangePct: s(t.targetChangePct),
    scope: t.scope ?? "",
    owner: t.owner ?? "",
    status: t.status,
    note: t.note ?? "",
  };
}

function num(s: string): number | null {
  const t = s.trim().replace(",", ".");
  if (!t) return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

/** Taslak → DTO; eksik zorunlu alanlarda `errors` döner (sunucu şemasıyla aynı kurallar). */
export function draftToUpsert(
  d: TargetDraft,
): { body: KpiTargetUpsert; errors: null } | { body: null; errors: Record<string, string> } {
  const v = KPI_TARGETS_COPY.targets.validation;
  const errors: Record<string, string> = {};
  const targetYear = num(d.targetYear);
  const baselineYear = num(d.baselineYear);
  const targetValue = num(d.targetValue);
  const targetChangePct = num(d.targetChangePct);
  if (targetYear === null || !Number.isInteger(targetYear))
    errors.targetYear = "Hedef yılı zorunlu";
  if (d.targetType === "relative") {
    if (targetChangePct === null) errors.targetChangePct = v.targetChangePct;
    if (baselineYear === null) errors.baselineYear = v.baselineYear;
  } else if (targetValue === null) {
    errors.targetValue = v.targetValue;
  }
  if (baselineYear !== null && targetYear !== null && baselineYear >= targetYear)
    errors.baselineYear = v.baselineYear;
  if (Object.keys(errors).length > 0) return { body: null, errors };
  const body: KpiTargetUpsert = {
    ...(d.id ? { id: d.id } : {}),
    metricId: d.metricId,
    entityId: d.entityId || null,
    targetType: d.targetType,
    baselineYear,
    baselineValue: num(d.baselineValue),
    targetYear: targetYear!,
    targetValue: d.targetType === "relative" ? null : targetValue,
    targetChangePct: d.targetType === "relative" ? targetChangePct : null,
    scope: d.scope.trim() || null,
    owner: d.owner.trim() || null,
    status: d.status,
    note: d.note.trim() || null,
  };
  return { body, errors: null };
}

/** Özet karşılaştırma tablosu için gösterge → en güncel aktif hedef (hedef yılı en yakın). */
export function pickPrimaryTarget(
  targets: KpiTargetWithProgress[],
): Map<string, KpiTargetWithProgress> {
  const out = new Map<string, KpiTargetWithProgress>();
  const sorted = [...targets]
    .filter((t) => t.entityId === null)
    .sort((a, b) => {
      const sa = a.status === "active" ? 0 : 1;
      const sb = b.status === "active" ? 0 : 1;
      return sa - sb || (a.targetYear ?? 9999) - (b.targetYear ?? 9999);
    });
  for (const t of sorted) if (!out.has(t.metricId)) out.set(t.metricId, t);
  return out;
}

export function targetsStrip(
  indicatorCount: number,
  targets: KpiTargetWithProgress[],
): { total: number; withTarget: number; achieved: number; onTrack: number } {
  const metrics = new Set(targets.map((t) => t.metricId));
  let achieved = 0;
  let onTrack = 0;
  for (const t of targets) {
    const tone = targetTone(t.progress);
    if (tone === "achieved") achieved += 1;
    else if (tone === "onTrack") onTrack += 1;
  }
  return { total: indicatorCount, withTarget: metrics.size, achieved, onTrack };
}

// ─── Disclosure ───────────────────────────────────────────────────────────────

export type DisclosureTab = "missing" | "all" | "notInSet";

export function disclosureRowMatches(row: KpiDisclosureRow, tab: DisclosureTab): boolean {
  switch (tab) {
    case "all":
      return row.status !== "not_in_set";
    case "missing":
      return (
        row.status !== "filled" && row.status !== "not_measured" && row.status !== "not_in_set"
      );
    case "notInSet":
      return row.status === "not_in_set";
  }
}

export function disclosureStatusLabel(s: KpiDisclosureRowStatus): string {
  return KPI_DISCLOSURE_ROW_STATUS_LABELS[s];
}

export function disclosureStatusTone(s: KpiDisclosureRowStatus): "ok" | "wait" | "xxx" | "muted" {
  if (s === "filled") return "ok";
  if (s === "calc_error" || s === "pending_xxx") return "wait";
  if (s === "not_measured") return "muted";
  return "xxx";
}

export function mappingLabel(mappingType: string): string {
  return KPI_TARGETS_COPY.disclosure.mapping[mappingType] ?? mappingType;
}

/** "Sette olmayan" sekmesi: aynı metrik birden çok kodla gelir → metrik başına tek satır, kodlar birleşik. */
export function groupNotInSet(rows: KpiDisclosureRow[]): {
  metricId: string;
  label: string;
  parentLabel: string | null;
  codes: string[];
  unit: string | null;
}[] {
  const map = new Map<
    string,
    {
      metricId: string;
      label: string;
      parentLabel: string | null;
      codes: string[];
      unit: string | null;
    }
  >();
  for (const r of rows) {
    if (r.status !== "not_in_set") continue;
    const g = map.get(r.metricId);
    if (g) g.codes.push(r.standardCode);
    else
      map.set(r.metricId, {
        metricId: r.metricId,
        label: r.label,
        parentLabel: r.parentLabel,
        codes: [r.standardCode],
        unit: r.unit,
      });
  }
  return [...map.values()];
}

export function readyTone(summary: Pick<KpiDisclosureSummary, "readyPct">): TargetTone {
  if (summary.readyPct >= 100) return "achieved";
  if (summary.readyPct >= 50) return "onTrack";
  return "behind";
}
